import {
  Button,
  createStyles,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core"
import { MouseEvent, useEffect, useRef } from "react"
import { colors } from "../../utils/theme"

interface TranscriptSessionControlsProps {
  isRecording: boolean
  isPaused: boolean
  isUploading: boolean
  isProcessing: boolean
  onStartRecording: (event: MouseEvent<HTMLElement>) => void
  onPauseRecording: () => void
  onResumeRecording: () => void
  onStopRecording: () => void
  registerElapsedTimeElement: (element: HTMLElement) => () => void
}

/**
 * Renders the header controls for starting, recording, and processing a
 * transcript session.
 */
export default function TranscriptSessionControls({
  isRecording,
  isPaused,
  isUploading,
  isProcessing,
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
  registerElapsedTimeElement,
}: TranscriptSessionControlsProps) {
  const classes = useStyles()
  const isBusy = isUploading || isProcessing
  const busyLabel = isUploading
    ? "Transcript uploading…"
    : "Transcript processing…"
  const processingPill = (
    <div className={classes.processingPill} tabIndex={isProcessing ? 0 : -1}>
      <span className={classes.processingDot} />
      <Typography variant="body2" className={classes.processingPillText}>
        {busyLabel}
      </Typography>
    </div>
  )

  if (isBusy) {
    return (
      <div className={classes.processingSessionStack} aria-live="polite">
        {isProcessing ? (
          <Tooltip
            title={
              <span>
                This usually takes a few minutes. You may leave this page while
                processing continues.
              </span>
            }
            placement="bottom"
          >
            {processingPill}
          </Tooltip>
        ) : (
          processingPill
        )}
      </div>
    )
  }

  if (isRecording || isPaused) {
    return (
      <div className={classes.recordingActionsRow}>
        <div className={classes.recordingPill}>
          <span className={classes.recordingDot} />
          <Typography variant="body2" className={classes.recordingPillText}>
            {isPaused ? "Paused" : "Recording"} ·{" "}
            <RecorderDuration
              registerElapsedTimeElement={registerElapsedTimeElement}
            />
          </Typography>
        </div>
        <Button
          className={classes.pauseSessionButton}
          disableElevation
          onClick={isPaused ? onResumeRecording : onPauseRecording}
        >
          {isPaused ? "Resume" : "Pause"}
        </Button>
        <Button
          className={classes.endSessionButton}
          disableElevation
          onClick={onStopRecording}
          disabled={isBusy}
        >
          End Session
        </Button>
      </div>
    )
  }

  return (
    <Button
      className={classes.startSessionButton}
      disableElevation
      onClick={onStartRecording}
      disabled={isBusy}
    >
      <span className={classes.startSessionDot} />
      Start Session
    </Button>
  )
}

function RecorderDuration({
  registerElapsedTimeElement,
}: {
  registerElapsedTimeElement: (element: HTMLElement) => () => void
}) {
  const durationElementRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const durationElement = durationElementRef.current
    return durationElement
      ? registerElapsedTimeElement(durationElement)
      : undefined
  }, [registerElapsedTimeElement])

  return <span ref={durationElementRef}>00:00</span>
}

const useStyles = makeStyles((theme) =>
  createStyles({
    recordingActionsRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      flexWrap: "wrap",
      [theme.breakpoints.down("sm")]: {
        justifyContent: "flex-start",
      },
    },
    recordingPill: {
      display: "flex",
      alignItems: "center",
      minHeight: 32,
      padding: theme.spacing(0.875, 2),
      borderRadius: 32,
      border: "1px solid #F5C9A6",
      backgroundColor: "#FDF1E8",
    },
    recordingDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      marginRight: theme.spacing(1.25),
      backgroundColor: colors.tertiary.tangerine,
      flexShrink: 0,
    },
    recordingPillText: {
      color: "#9A4B12",
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.23,
    },
    startSessionButton: {
      minWidth: 0,
      padding: theme.spacing(1, 2.25),
      borderRadius: 32,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 13.5,
      lineHeight: 1.2,
      color: colors.primary.midnight,
      backgroundColor: colors.primary.cloud,
      border: `1px solid ${colors.primary.cloud}`,
      "&:hover": {
        backgroundColor: colors.primary.cloud,
      },
    },
    pauseSessionButton: {
      minWidth: 0,
      padding: theme.spacing(0.875, 2),
      borderRadius: 32,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.2,
      color: colors.primary.midnight,
      backgroundColor: colors.neutral.white,
      border: `1px solid ${colors.primary.cloud}`,
      opacity: 1,
      "&.Mui-disabled": {
        color: colors.primary.midnight,
        borderColor: colors.primary.cloud,
        backgroundColor: colors.neutral.white,
        opacity: 1,
      },
    },
    endSessionButton: {
      minWidth: 0,
      padding: theme.spacing(0.875, 2.5),
      borderRadius: 32,
      textTransform: "none",
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.23,
      color: colors.primary.midnight,
      backgroundColor: colors.primary.cloud,
      border: `1px solid ${colors.primary.cloud}`,
      "&:hover": {
        backgroundColor: colors.primary.cloud,
      },
    },
    startSessionDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      marginRight: theme.spacing(1.25),
      backgroundColor: colors.tertiary.tangerine,
      flexShrink: 0,
    },
    processingSessionStack: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 3,
      width: 239.375,
    },
    processingPill: {
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      width: 184.688,
      height: 32,
      padding: theme.spacing(0, 2),
      marginLeft: 46.69,
      border: `1px solid ${colors.primary.ice}`,
      borderRadius: 32,
      backgroundColor: colors.primary.cloud,
    },
    processingDot: {
      width: 9,
      height: 9,
      borderRadius: "50%",
      marginRight: theme.spacing(1),
      backgroundColor: colors.primary.ice,
      flexShrink: 0,
    },
    processingPillText: {
      color: colors.neutral.denim,
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.23,
      whiteSpace: "nowrap",
    },
  })
)
